import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useSetReturnMode } from "@babackapp/return-widget-components/hooks/use-set-return-mode";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useWidgetParams } from "./use-widget-params";
import { ReturnMode } from "@babackapp/return-widget-components/types";

const returnModeSchema = z
  .object({
    returnMode: z.string(),
    pickupDate: z.string().optional(),
    storeId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.returnMode === "carrier" && !data.pickupDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pickupDate"],
        message: "required",
      });
    }
    if (data.returnMode === "store" && !data.storeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["storeId"],
        message: "required",
      });
    }
  });

type ReturnModeFormValues = {
  returnMode: ReturnMode;
  pickupDate?: string;
  storeId?: string;
};

export function useReturnModeForm() {
  const navigate = useNavigate();
  const { shopId, orderId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const setReturnMode = useSetReturnMode(orderId);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultPickupDate = tomorrow.toISOString().split("T")[0];

  const form = useForm<ReturnModeFormValues>({
    resolver: zodResolver(returnModeSchema),
    defaultValues: {
      returnMode: "carrier",
      pickupDate: defaultPickupDate,
      storeId: undefined,
    },
  });

  const availableReturnModes: ReturnMode[] = ["carrier"];

  const hasStoreReturns = shop.returnToStore?.activated === true;
  if (hasStoreReturns) {
    availableReturnModes.push("store");
  }

  const selectableStores = (shop.returnToStore?.stores || []).filter(
    (store) => store.selectableByUser,
  );

  const onSubmit = form.handleSubmit(async (values) => {
    setReturnMode(
      values.returnMode,
      values.returnMode === "store" ? (values.storeId ?? null) : null,
      values.returnMode === "carrier" ? (values.pickupDate ?? null) : null,
    );
    navigate("../address");
  });

  return {
    form,
    onSubmit,
    availableReturnModes,
    selectableStores,
    hasStoreReturns,
  };
}
